import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, User, ArrowRight, AlertCircle } from "lucide-react";

// Demo client-hub login. Uses local session only — this is intentionally
// separate from the platform admin auth (which lives at /login) and exists
// so Kit can walk demos through the Nari AI operator console.
// Demo credentials: username `test` · password `test1`.
const DEMO_USER = "test";
const DEMO_PASS = "test1";
const SESSION_KEY = "nariai_clients_session";

export default function ClientsLogin() {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    document.title = "Sign in · Nari AI Operator Console";
    const prev = document.body.style.background;
    document.body.style.background = "#FFFFFF";
    return () => { document.body.style.background = prev; };
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    // Small delay to feel deliberate, not laggy
    setTimeout(() => {
      // Demo creds — be tolerant of mobile autocap + trailing whitespace so
      // Kit can type "Test1" or " test1 " on a phone and still get in.
      const uNorm = u.trim().toLowerCase();
      const pNorm = p.trim().toLowerCase();
      if (uNorm === DEMO_USER && pNorm === DEMO_PASS) {
        try {
          localStorage.setItem(
            SESSION_KEY,
            JSON.stringify({ user: DEMO_USER, ts: Date.now() })
          );
        } catch { /* ignore */ }
        nav("/clients/hub", { replace: true });
      } else {
        setErr("Invalid credentials. Try test / test1");
        setLoading(false);
      }
    }, 260);
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6 relative overflow-hidden"
      style={{ background: "#FFFFFF", fontFamily: "'Inter', system-ui, sans-serif", color: "#111" }}
      data-testid="clients-login"
    >
      {/* Corner marks */}
      {["tl", "tr", "bl", "br"].map((pos) => (
        <div key={pos} className="absolute w-6 h-6" style={{
          top: pos.startsWith("t") ? 20 : "auto",
          bottom: pos.startsWith("b") ? 20 : "auto",
          left: pos.endsWith("l") ? 20 : "auto",
          right: pos.endsWith("r") ? 20 : "auto",
          borderTop: pos.startsWith("t") ? "1px solid rgba(0,0,0,0.22)" : "none",
          borderBottom: pos.startsWith("b") ? "1px solid rgba(0,0,0,0.22)" : "none",
          borderLeft: pos.endsWith("l") ? "1px solid rgba(0,0,0,0.22)" : "none",
          borderRight: pos.endsWith("r") ? "1px solid rgba(0,0,0,0.22)" : "none",
        }} />
      ))}

      <Link
        to="/clients"
        className="absolute top-6 left-6 md:top-8 md:left-10 text-[10px] uppercase tracking-[0.28em] font-bold hover:text-black transition-colors"
        style={{ color: "rgba(0,0,0,0.45)" }}
        data-testid="login-back"
      >
        ← Splash
      </Link>

      <div className="w-full max-w-[420px] relative z-10">
        <div className="flex items-center gap-2 mb-6" data-testid="login-mark">
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#22C55E", boxShadow: "0 0 10px rgba(34,197,94,0.9)" }} />
          <div className="text-[10px] uppercase tracking-[0.32em] font-bold" style={{ color: "rgba(0,0,0,0.55)" }}>
            <span style={{ color: "#B8BEC7" }}>Nari</span>
            <span style={{ color: "#22C55E" }}>AI</span> · Operator Console
          </div>
        </div>

        <h1 className="font-black tracking-[-0.02em] leading-[1.02] mb-2"
            style={{ fontSize: "clamp(1.8rem, 3.6vw, 2.4rem)", fontFamily: "'Cabinet Grotesk', 'Inter', system-ui, sans-serif" }}
            data-testid="login-heading">
          Sign in to continue.
        </h1>
        <p className="text-sm mb-8" style={{ color: "rgba(0,0,0,0.55)" }}>
          One console. Every company you run.
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div className="relative">
            <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "rgba(0,0,0,0.4)" }} />
            <input
              type="text"
              value={u}
              onChange={(e) => setU(e.target.value)}
              placeholder="Username"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
              className="w-full pl-11 pr-4 h-14 rounded-lg text-sm outline-none transition-all"
              style={{
                background: "#FFFFFF",
                border: "1px solid rgba(0,0,0,0.14)",
                color: "#111",
              }}
              onFocus={(e) => { e.target.style.borderColor = "rgba(34,197,94,0.7)"; e.target.style.boxShadow = "0 0 0 3px rgba(34,197,94,0.14)"; }}
              onBlur={(e) => { e.target.style.borderColor = "rgba(0,0,0,0.14)"; e.target.style.boxShadow = "none"; }}
              data-testid="login-username"
            />
          </div>

          <div className="relative">
            <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "rgba(0,0,0,0.4)" }} />
            <input
              type="password"
              value={p}
              onChange={(e) => setP(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="w-full pl-11 pr-4 h-14 rounded-lg text-sm outline-none transition-all"
              style={{
                background: "#FFFFFF",
                border: "1px solid rgba(0,0,0,0.14)",
                color: "#111",
              }}
              onFocus={(e) => { e.target.style.borderColor = "rgba(34,197,94,0.7)"; e.target.style.boxShadow = "0 0 0 3px rgba(34,197,94,0.14)"; }}
              onBlur={(e) => { e.target.style.borderColor = "rgba(0,0,0,0.14)"; e.target.style.boxShadow = "none"; }}
              data-testid="login-password"
            />
          </div>

          {err && (
            <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-md"
                 style={{ background: "rgba(220,38,38,0.08)", color: "#B91C1C" }}
                 data-testid="login-error">
              <AlertCircle size={14} />
              <span>{err}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full h-14 rounded-lg inline-flex items-center justify-center gap-3 font-black uppercase tracking-[0.28em] text-xs transition-all"
            style={{
              background: "#0A0A0A",
              color: "#FFFFFF",
              boxShadow: "0 18px 40px -14px rgba(0,0,0,0.35), 0 0 24px -8px rgba(34,197,94,0.5)",
              opacity: loading ? 0.7 : 1,
            }}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.letterSpacing = "0.34em")}
            onMouseLeave={(e) => !loading && (e.currentTarget.style.letterSpacing = "0.28em")}
            data-testid="login-submit"
          >
            <span>{loading ? "Authenticating…" : "Enter Console"}</span>
            {!loading && <ArrowRight size={14} style={{ color: "#22C55E" }} />}
          </button>
        </form>

        <div className="mt-6 text-[10px] uppercase tracking-[0.22em] font-mono"
             style={{ color: "rgba(0,0,0,0.35)" }}
             data-testid="login-demo-hint">
          Demo login · <span style={{ color: "rgba(0,0,0,0.6)" }}>test</span> / <span style={{ color: "rgba(0,0,0,0.6)" }}>test1</span>
        </div>
      </div>
    </div>
  );
}

export { SESSION_KEY as CLIENTS_SESSION_KEY };
